import React, { useState, useEffect } from 'react';
import { Spinner, Alert, Button, Card, Form } from 'react-bootstrap';
import { getTeacherQuestionnaireStatus, submitTeacherScreening } from '../../api/profile';

export const TeacherScreeningTab = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [statusData, setStatusData] = useState(null);

  // Form State
  const [form, setForm] = useState({
    yearsOfExperience: '',
    languagesQualified: '',
    canSpeakClearly: '',
    hasReliableInternet: '',
    hasTeachingCertificate: '',
    comfortableWithGroupAndOneOnOne: '',
    teachingStyle: '',
    studentEngagementMethods: '',
    hasQuietWellLitBackground: '',
    profile_url: '',
  });

  const [certificateFile, setCertificateFile] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getTeacherQuestionnaireStatus();
      if (res && res.data) {
        setStatusData(res.data);
        if (res.data.hasQuestionnaire && res.data.questionnaire) {
          const q = res.data.questionnaire;
          setForm({
            yearsOfExperience: q.yearsOfExperience || '',
            languagesQualified: q.languagesQualified || '',
            canSpeakClearly: q.canSpeakClearly || '',
            hasReliableInternet: q.hasReliableInternet || '',
            hasTeachingCertificate: q.hasTeachingCertificate || '',
            comfortableWithGroupAndOneOnOne: q.comfortableWithGroupAndOneOnOne || '',
            teachingStyle: q.teachingStyle || '',
            studentEngagementMethods: q.studentEngagementMethods || '',
            hasQuietWellLitBackground: q.hasQuietWellLitBackground || '',
            profile_url: res.data.profile_url || '',
          });
        }
      }
    } catch (err) {
      console.error('Error fetching questionnaire status:', err);
      setError(err.response?.data?.message || 'Failed to retrieve screening questionnaire status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCertificateFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic required validation
    const required = [
      'yearsOfExperience',
      'languagesQualified',
      'canSpeakClearly',
      'hasReliableInternet',
      'hasTeachingCertificate',
      'comfortableWithGroupAndOneOnOne',
      'teachingStyle',
      'studentEngagementMethods',
      'hasQuietWellLitBackground',
    ];

    for (const field of required) {
      if (!form[field] || form[field].trim() === '') {
        setError('Please answer all questions before submitting.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);

      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(key, form[key]);
      });

      if (certificateFile) {
        formData.append('certificateFile', certificateFile);
      }

      const res = await submitTeacherScreening(formData);
      setSuccessMsg(res.message || 'Questionnaire submitted successfully!');
      await fetchStatus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting screening questionnaire:', err);
      setError(err.response?.data?.message || 'Failed to submit questionnaire.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReapply = () => {
    setStatusData({
      hasQuestionnaire: false,
      status: null,
    });
    setCertificateFile(null);
    setError(null);
    setSuccessMsg(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Loading questionnaire details...</p>
      </div>
    );
  }

  const hasSubmitted = statusData?.hasQuestionnaire;
  const status = statusData?.status; // 'pending' | 'approved' | 'rejected'
  const notes = statusData?.notes;

  return (
    <div className="teacher-screening-container">
      {successMsg && (
        <Alert variant="success" onClose={() => setSuccessMsg(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-check-circle-fill me-2"></i>{successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="rounded-3 shadow-sm mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
        </Alert>
      )}

      {/* Case 1: Pending Review */}
      {hasSubmitted && status === 'pending' && (
        <Card className="border-0 shadow-sm rounded-4 p-5 text-center bg-white mb-4">
          <div className="pending-icon-wrapper mb-4">
            <div className="icon-pulse bg-warning bg-opacity-10 text-warning rounded-circle d-inline-flex p-4">
              <i className="bi bi-hourglass-split fs-1"></i>
            </div>
          </div>
          <h3 className="fw-black text-gray-900 mb-2">Screening Application Under Review</h3>
          <p className="text-muted mx-auto mb-4" style={{ maxWidth: '500px' }}>
            Thank you for submitting your screening questionnaire. Our administration team is currently reviewing your application and checking your qualifications. This process usually takes 2-3 business days. We will notify you once a decision is made.
          </p>
          {statusData?.submittedAt && (
            <div className="text-xs text-muted">
              Submitted on: {new Date(statusData.submittedAt).toLocaleString()}
            </div>
          )}
        </Card>
      )}

      {/* Case 2: Approved */}
      {hasSubmitted && status === 'approved' && (
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4 premium-active-card border-top border-5 border-success">
          <Card.Body className="p-5 text-center bg-white">
            <div className="badge-glowing-wrapper mb-4 text-success">
              <i className="bi bi-patch-check-fill glow-icon fs-1"></i>
            </div>
            <h3 className="fw-black text-success mb-2">Screening Application Approved!</h3>
            <p className="text-muted mx-auto mb-0" style={{ maxWidth: '500px' }}>
              Congratulations! You are officially verified and approved to teach on YooSpeak. You can now publish classes, create communities, and start receiving bookings from students.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Case 3: Rejected */}
      {hasSubmitted && status === 'rejected' && (
        <Card className="border-0 shadow-sm rounded-4 p-5 text-center bg-white mb-4 border-top border-5 border-danger">
          <div className="rejected-icon-wrapper mb-4">
            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-4">
              <i className="bi bi-x-circle-fill fs-1"></i>
            </div>
          </div>
          <h3 className="fw-black text-gray-900 mb-2">Application Rejected</h3>
          <p className="text-muted mx-auto" style={{ maxWidth: '500px' }}>
            Unfortunately, your screening application did not meet our verification criteria.
          </p>

          {notes && (
            <Alert variant="danger" className="mx-auto rounded-3 my-4 text-start" style={{ maxWidth: '500px' }}>
              <h6 className="fw-bold mb-1"><i className="bi bi-info-circle me-1"></i>Reviewer's Feedback:</h6>
              <p className="text-sm m-0">{notes}</p>
            </Alert>
          )}

          <div className="mt-4">
            <Button variant="primary" onClick={handleReapply} className="px-5 py-2.5 rounded-pill fw-bold">
              Re-apply / Submit New Form
            </Button>
          </div>
        </Card>
      )}

      {/* Case 4: No Submission / Form View */}
      {(!hasSubmitted || !status) && (
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4 bg-white">
          <div className="p-4 border-bottom text-center">
            <h3 className="fw-black text-gray-900 mb-1">Teacher Screening Questionnaire</h3>
            <p className="text-muted m-0">Your answers will help us decide if you qualify to teach on the platform.</p>
          </div>
          <Card.Body className="p-4 p-md-5">
            <Form onSubmit={handleSubmit}>

              {/* Question 1 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  1. Do you have previous teaching experience? If yes, how many years?
                </Form.Label>
                <Form.Control
                  type="text"
                  name="yearsOfExperience"
                  value={form.yearsOfExperience}
                  onChange={handleChange}
                  placeholder="Enter experience"
                  className="profile-input py-3 rounded-3"
                  required
                />
              </Form.Group>

              {/* Question 2 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  2. Which language(s) are you qualified and confident to teach?
                </Form.Label>
                <Form.Control
                  type="text"
                  name="languagesQualified"
                  value={form.languagesQualified}
                  onChange={handleChange}
                  placeholder="Enter languages"
                  className="profile-input py-3 rounded-3"
                  required
                />
              </Form.Group>

              {/* Question 3 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  3. Can you speak and communicate clearly in that language?
                </Form.Label>
                <div className="d-flex gap-4 mt-1">
                  <Form.Check
                    type="radio"
                    id="speak-yes"
                    name="canSpeakClearly"
                    value="Yes"
                    label="Yes"
                    checked={form.canSpeakClearly === 'Yes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="speak-no"
                    name="canSpeakClearly"
                    value="No"
                    label="No"
                    checked={form.canSpeakClearly === 'No'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                </div>
              </Form.Group>

              {/* Question 4 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  4. Do you have a reliable internet connection, camera, and microphone for teaching?
                </Form.Label>
                <div className="d-flex gap-4 mt-1">
                  <Form.Check
                    type="radio"
                    id="internet-yes"
                    name="hasReliableInternet"
                    value="Yes"
                    label="Yes"
                    checked={form.hasReliableInternet === 'Yes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="internet-no"
                    name="hasReliableInternet"
                    value="No"
                    label="No"
                    checked={form.hasReliableInternet === 'No'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                </div>
              </Form.Group>

              {/* Question 5 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  5. Do you hold any teaching certificate or diploma?
                </Form.Label>
                <div className="d-flex gap-4 mt-1">
                  <Form.Check
                    type="radio"
                    id="cert-yes"
                    name="hasTeachingCertificate"
                    value="Yes"
                    label="Yes"
                    checked={form.hasTeachingCertificate === 'Yes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="cert-no"
                    name="hasTeachingCertificate"
                    value="No"
                    label="No"
                    checked={form.hasTeachingCertificate === 'No'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                </div>
              </Form.Group>

              {/* Question 6 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  6. Are you comfortable teaching both group and one-on-one sessions using the app?
                </Form.Label>
                <div className="d-flex flex-column gap-2 mt-1">
                  <Form.Check
                    type="radio"
                    id="comfortable-yes"
                    name="comfortableWithGroupAndOneOnOne"
                    value="Yes"
                    label="Yes"
                    checked={form.comfortableWithGroupAndOneOnOne === 'Yes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="comfortable-sometimes"
                    name="comfortableWithGroupAndOneOnOne"
                    value="Sometimes"
                    label="Sometimes"
                    checked={form.comfortableWithGroupAndOneOnOne === 'Sometimes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="comfortable-no"
                    name="comfortableWithGroupAndOneOnOne"
                    value="No"
                    label="No"
                    checked={form.comfortableWithGroupAndOneOnOne === 'No'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                </div>
              </Form.Group>

              {/* Question 7 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  7. How would you describe your teaching style in a few words?
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="teachingStyle"
                  value={form.teachingStyle}
                  onChange={handleChange}
                  placeholder="Enter teaching style"
                  className="profile-input rounded-3"
                  required
                />
              </Form.Group>

              {/* Question 8 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  8. How do you keep your students engaged and motivated?
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="studentEngagementMethods"
                  value={form.studentEngagementMethods}
                  onChange={handleChange}
                  placeholder="Enter engagement methods"
                  className="profile-input rounded-3"
                  required
                />
              </Form.Group>

              {/* Question 9 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  9. Can you teach in a quiet, background-noise-free space while dressed professionally on camera?
                </Form.Label>
                <div className="d-flex gap-4 mt-1">
                  <Form.Check
                    type="radio"
                    id="noise-yes"
                    name="hasQuietWellLitBackground"
                    value="Yes"
                    label="Yes"
                    checked={form.hasQuietWellLitBackground === 'Yes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="noise-sometimes"
                    name="hasQuietWellLitBackground"
                    value="Sometimes"
                    label="Sometimes"
                    checked={form.hasQuietWellLitBackground === 'Sometimes'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                  <Form.Check
                    type="radio"
                    id="noise-no"
                    name="hasQuietWellLitBackground"
                    value="No"
                    label="No"
                    checked={form.hasQuietWellLitBackground === 'No'}
                    onChange={handleChange}
                    className="fw-medium text-gray-800"
                  />
                </div>
              </Form.Group>

              {/* Question 10 */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  10. Upload your resume or teaching certificate (Optional):
                </Form.Label>
                <div className="d-flex align-items-center gap-3">
                  <input
                    type="file"
                    id="certificate-file-input"
                    className="d-none"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="certificate-file-input"
                    className="btn btn-outline-primary px-4 py-2 rounded-pill fw-bold cursor-pointer"
                  >
                    Add
                  </label>
                  {certificateFile ? (
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-file-earmark-check-fill text-success fs-5"></i>
                      <span className="text-sm fw-semibold text-gray-700 text-truncate" style={{ maxWidth: '200px' }}>
                        {certificateFile.name}
                      </span>
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => setCertificateFile(null)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted">No file selected (Supports PDF, JPG, PNG)</span>
                  )}
                </div>
              </Form.Group>

              {/* Question 11 */}
              <Form.Group className="mb-5">
                <Form.Label className="fw-bold text-gray-900 mb-2">
                  11. Enter profile link (e.g., Facebook, LinkedIn, Instagram, TikTok, YouTube)
                </Form.Label>
                <Form.Control
                  type="url"
                  name="profile_url"
                  value={form.profile_url}
                  onChange={handleChange}
                  placeholder="Paste your profile link"
                  className="profile-input py-3 rounded-3"
                />
              </Form.Group>

              {/* Submit */}
              <div className="text-center">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                  className="px-5 py-3 rounded-pill fw-bold shadow-sm"
                  style={{ width: '100%', maxWidth: '300px' }}
                >
                  {submitting ? <><Spinner animation="border" size="sm" className="me-2" />Submitting...</> : 'Submit'}
                </Button>
              </div>

            </Form>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};
